<template>
  <div v-if="visible" class="pfi-overlay" role="dialog" aria-modal="true" @click="close">
    <div class="pfi-card" @click.stop>
      <!-- Header -->
      <div class="pfi-head">
        <h2>{{ $t('seedance.promptFromImage.title') }}</h2>
        <button type="button" class="pfi-close" :aria-label="$t('seedance.common.cancel')" @click="close">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
        </button>
      </div>

      <!-- Body：桌面左右两栏 / 移动端上下堆叠 -->
      <div class="pfi-body">
        <!-- 左：操作面板 -->
        <div class="pfi-left">
          <!-- 选图 -->
          <div v-if="selectedImage" class="pfi-picked">
            <img :src="selectedImage" alt="" />
            <button type="button" class="pfi-btn ghost full" :disabled="generating" @click="pickerOpen = true">
              {{ $t('seedance.promptFromImage.changeImage') }}
            </button>
          </div>
          <button v-else type="button" class="pfi-drop" @click="pickerOpen = true">
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="9" cy="9" r="2"/><path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21"/></svg>
            <span>{{ $t('seedance.promptFromImage.selectImage') }}</span>
          </button>

          <!-- Prompt Mode -->
          <div class="pfi-field">
            <span class="pfi-label">{{ $t('seedance.promptFromImage.promptMode') }}</span>
            <div class="pfi-seg">
              <button type="button" class="pfi-seg-btn" :class="{ active: mode === 'image' }" :disabled="generating" @click="mode = 'image'">
                {{ $t('seedance.promptFromImage.modeImage') }}
              </button>
              <button type="button" class="pfi-seg-btn" :class="{ active: mode === 'video' }" :disabled="generating" @click="mode = 'video'">
                {{ $t('seedance.promptFromImage.modeVideo') }}
              </button>
            </div>
            <span class="pfi-hint">{{ mode === 'video' ? $t('seedance.promptFromImage.modeVideoDesc') : $t('seedance.promptFromImage.modeImageDesc') }}</span>
          </div>

          <!-- 语言下拉 -->
          <div class="pfi-field">
            <span class="pfi-label">{{ $t('seedance.promptFromImage.promptLanguage') }}</span>
            <select v-model="language" class="pfi-select" :disabled="generating">
              <option v-for="l in LANGUAGES" :key="l.value" :value="l.value">{{ l.label }}</option>
            </select>
          </div>

          <!-- 生成 -->
          <button type="button" class="pfi-btn primary full" :disabled="!selectedImage || generating" @click="generate">
            <span v-if="generating" class="pfi-spin" />
            <svg v-else width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .962 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.962 0z"/></svg>
            {{ generating ? $t('seedance.promptFromImage.generating') : $t('seedance.promptFromImage.generate') }}
          </button>
        </div>

        <!-- 右：历史记录 -->
        <div class="pfi-right">
          <h3 class="pfi-h3">
            {{ $t('seedance.promptFromImage.history') }}
            <span v-if="history.length" class="pfi-count">({{ history.length }})</span>
          </h3>

          <div v-if="loadingHistory" class="pfi-center"><span class="pfi-spin dark" /></div>
          <p v-else-if="!history.length" class="pfi-empty">{{ $t('seedance.promptFromImage.empty') }}</p>
          <div v-else class="pfi-list">
            <div v-for="item in history" :key="item.id" class="pfi-item" :class="{ processing: item.status === 'processing' }">
              <div class="pfi-thumb">
                <img v-if="item.imageUrl" :src="item.imageUrl" alt="" :class="{ pulse: item.status === 'processing' }" />
                <span class="pfi-mode-badge">
                  <svg v-if="item.mode === 'video'" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="m22 8-6 4 6 4V8Z"/><rect x="2" y="6" width="14" height="12" rx="2"/></svg>
                  <svg v-else width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="9" cy="9" r="2"/><path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21"/></svg>
                </span>
              </div>

              <div v-if="item.status === 'processing'" class="pfi-analyzing">
                <span class="pfi-spin dark" />
                <span>{{ $t('seedance.promptFromImage.analyzing') }}</span>
              </div>
              <template v-else>
                <p class="pfi-text" :class="{ expanded: expanded.has(item.id) }" @click="toggle(item.id)">{{ item.prompt }}</p>
                <div class="pfi-item-actions">
                  <button type="button" class="pfi-btn small" @click="use(item.prompt)">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                    {{ $t('seedance.promptFromImage.use') }}
                  </button>
                  <button type="button" class="pfi-chevron" :aria-label="expanded.has(item.id) ? 'Collapse' : 'Expand'" @click="toggle(item.id)">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" :style="{ transform: expanded.has(item.id) ? 'rotate(180deg)' : 'none' }"><polyline points="6 9 12 15 18 9"/></svg>
                  </button>
                </div>
              </template>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 素材选择器（复用，单选图片；z-index 高于本弹窗） -->
    <AssetPickerDialog v-model="pickerOpen" type="image" :title="$t('seedance.promptFromImage.selectImage')" @pick="onPick" />
  </div>
</template>

<script setup lang="ts">
import AssetPickerDialog from '~/components/seedance/AssetPickerDialog.vue';
import api from '~/api';
import { useSnackbarStore } from '~/composables/useSnackBarStore';

const props = defineProps<{ modelValue: boolean }>();
const emit = defineEmits<{
  (e: 'update:modelValue', v: boolean): void;
  (e: 'use', prompt: string): void;
}>();

const { t } = useI18n();
const snackbar = useSnackbarStore();

const LANGUAGES = [
  { value: 'en', label: 'English' },
  { value: 'zh', label: '中文' },
  { value: 'tw', label: '繁體中文' },
  { value: 'ja', label: '日本語' },
  { value: 'ko', label: '한국어' },
  { value: 'de', label: 'Deutsch' },
  { value: 'fr', label: 'Français' },
  { value: 'es', label: 'Español' },
  { value: 'it', label: 'Italiano' },
  { value: 'pt', label: 'Português' },
  { value: 'nl', label: 'Nederlands' },
];

interface HistoryItem {
  id: string;
  prompt: string;
  imageUrl: string;
  mode: 'image' | 'video';
  status?: 'processing';
}

const visible = computed({
  get: () => props.modelValue,
  set: (v) => emit('update:modelValue', v),
});

const pickerOpen = ref(false);
const selectedImage = ref<string | null>(null);
const mode = ref<'image' | 'video'>('video');
const language = ref('en');
const generating = ref(false);
const history = ref<HistoryItem[]>([]);
const loadingHistory = ref(false);
const expanded = reactive(new Set<string>());

const toggle = (id: string) => {
  if (expanded.has(id)) expanded.delete(id);
  else expanded.add(id);
};

const close = () => {
  visible.value = false;
};

const onPick = (assets: { url: string }[]) => {
  const url = assets?.[0]?.url;
  if (url) selectedImage.value = url;
  pickerOpen.value = false;
};

async function loadHistory() {
  loadingHistory.value = true;
  try {
    const res = await api.imageToPromptHistory({ limit: 50 });
    history.value = (res.items ?? []).map((i) => ({
      id: i.id,
      prompt: i.prompt,
      imageUrl: i.imageUrl,
      mode: i.mode,
    }));
  } catch (err) {
    console.error('[PromptFromImage] load history failed', err);
  } finally {
    loadingHistory.value = false;
  }
}

watch(visible, (v) => {
  if (v) loadHistory();
});

async function generate() {
  if (!selectedImage.value || generating.value) return;
  generating.value = true;
  const placeholderId = `tmp-${Date.now()}`;
  history.value.unshift({
    id: placeholderId,
    prompt: '',
    imageUrl: selectedImage.value,
    mode: mode.value,
    status: 'processing',
  });
  try {
    const res = await api.imageToPromptGenerate({
      imageUrl: selectedImage.value,
      language: language.value,
      mode: mode.value,
    });
    const idx = history.value.findIndex((i) => i.id === placeholderId);
    if (idx !== -1) {
      history.value[idx] = {
        id: placeholderId,
        prompt: res.prompt,
        imageUrl: selectedImage.value!,
        mode: mode.value,
      };
    }
  } catch (err: any) {
    history.value = history.value.filter((i) => i.id !== placeholderId);
    const msg = String(err?.message ?? '');
    // 402 由 axios 拦截器自动弹充值，这里只处理其它错误
    if (err?.status !== 402 && err?.response?.status !== 402) {
      if (msg.includes('CONTENT_REFUSED')) {
        snackbar.showError(t('seedance.promptFromImage.contentRefused'));
      } else {
        snackbar.showError(t('seedance.promptFromImage.serviceBusy'));
      }
    }
  } finally {
    generating.value = false;
  }
}

function use(prompt: string) {
  emit('use', prompt);
  snackbar.showSuccess(t('seedance.promptFromImage.added'));
  close();
}
</script>

<style scoped lang="scss">
.pfi-overlay {
  position: fixed;
  inset: 0;
  z-index: 80;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(6px);
  padding: 1rem;
}
.pfi-card {
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 900px;
  height: 86vh;
  max-height: 86vh;
  border-radius: 1rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: #1a1a1f;
  color: #fff;
  overflow: hidden;
}
.pfi-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.25rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  flex-shrink: 0;
}
.pfi-head h2 { font-size: 1rem; font-weight: 700; margin: 0; }
.pfi-close {
  display: flex;
  padding: 0.25rem;
  border: none;
  border-radius: 0.375rem;
  background: transparent;
  color: rgba(255, 255, 255, 0.6);
  cursor: pointer;
}
.pfi-close:hover { background: rgba(255, 255, 255, 0.08); color: #fff; }

.pfi-body {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  min-height: 0;
}
@media (min-width: 1024px) {
  .pfi-body { flex-direction: row; overflow: hidden; }
}

.pfi-left {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1.25rem;
}
@media (min-width: 1024px) {
  .pfi-left { width: 340px; flex-shrink: 0; overflow-y: auto; border-right: 1px solid rgba(255, 255, 255, 0.08); }
}

.pfi-picked { display: flex; flex-direction: column; gap: 0.5rem; }
.pfi-picked img {
  width: 100%;
  max-height: 13rem;
  object-fit: contain;
  border-radius: 0.5rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.04);
}
.pfi-drop {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  height: 10rem;
  border: 2px dashed rgba(255, 255, 255, 0.18);
  border-radius: 0.5rem;
  background: transparent;
  color: rgba(255, 255, 255, 0.55);
  cursor: pointer;
  font-size: 0.85rem;
  font-weight: 500;
}
.pfi-drop:hover { border-color: #d9466f; color: #fff; }

.pfi-field { display: flex; flex-direction: column; gap: 0.4rem; }
.pfi-label { font-size: 0.8rem; font-weight: 600; }
.pfi-hint { font-size: 0.7rem; color: rgba(255, 255, 255, 0.45); }
.pfi-seg {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.25rem;
  padding: 0.25rem;
  border-radius: 0.5rem;
  background: rgba(255, 255, 255, 0.06);
}
.pfi-seg-btn {
  padding: 0.45rem;
  border: none;
  border-radius: 0.375rem;
  background: transparent;
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.8rem;
  cursor: pointer;
}
.pfi-seg-btn.active { background: rgba(255, 255, 255, 0.14); color: #fff; font-weight: 600; }
.pfi-select {
  width: 100%;
  padding: 0.5rem 0.6rem;
  border-radius: 0.5rem;
  border: 1px solid rgba(255, 255, 255, 0.14);
  background: rgba(255, 255, 255, 0.04);
  color: #fff;
  font-size: 0.85rem;
  cursor: pointer;
}
.pfi-select option { background: #1a1a1f; color: #fff; }

.pfi-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  border: none;
  border-radius: 0.5rem;
  cursor: pointer;
  font-weight: 600;
  transition: opacity 0.15s, background 0.15s;
}
.pfi-btn.full { width: 100%; }
.pfi-btn.primary { padding: 0.7rem 1rem; background: linear-gradient(90deg, #d946ef, #f43f5e); color: #fff; font-size: 0.9rem; }
.pfi-btn.primary:disabled { opacity: 0.5; cursor: not-allowed; }
.pfi-btn.ghost { padding: 0.5rem; background: rgba(255, 255, 255, 0.08); color: #fff; font-size: 0.8rem; }
.pfi-btn.small { padding: 0.35rem 0.6rem; background: rgba(255, 255, 255, 0.12); color: #fff; font-size: 0.75rem; }
.pfi-btn.small:hover { background: rgba(255, 255, 255, 0.2); }

.pfi-right {
  display: flex;
  flex-direction: column;
  padding: 1.25rem;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
}
@media (min-width: 1024px) {
  .pfi-right { flex: 1; overflow-y: auto; border-top: none; }
}
.pfi-h3 { font-size: 0.85rem; font-weight: 700; margin: 0 0 0.6rem; }
.pfi-count { color: rgba(255, 255, 255, 0.5); font-weight: 400; margin-left: 0.3rem; }
.pfi-center { display: flex; justify-content: center; padding: 2rem 0; }
.pfi-empty { text-align: center; color: rgba(255, 255, 255, 0.5); font-size: 0.85rem; padding: 2rem 0; }

.pfi-list { display: flex; flex-direction: column; gap: 0.5rem; }
.pfi-item {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 0.625rem;
  border-radius: 0.5rem;
  border: 1px solid rgba(255, 255, 255, 0.08);
  background: rgba(255, 255, 255, 0.02);
}
.pfi-item.processing { border-color: rgba(217, 70, 239, 0.3); background: rgba(217, 70, 239, 0.05); }
.pfi-thumb { position: relative; flex-shrink: 0; }
.pfi-thumb img { width: 2.75rem; height: 2.75rem; border-radius: 0.375rem; object-fit: cover; }
.pfi-thumb img.pulse { animation: pfi-pulse 1.2s ease-in-out infinite; }
.pfi-mode-badge {
  position: absolute;
  right: -4px;
  bottom: -4px;
  display: flex;
  padding: 2px;
  border-radius: 4px;
  border: 1px solid rgba(255, 255, 255, 0.15);
  background: #1a1a1f;
  color: rgba(255, 255, 255, 0.6);
}
.pfi-analyzing { display: flex; align-items: center; gap: 0.5rem; flex: 1; color: rgba(255, 255, 255, 0.6); font-size: 0.85rem; padding: 0.4rem 0; }
.pfi-text {
  flex: 1;
  min-width: 0;
  margin: 0;
  font-size: 0.85rem;
  line-height: 1.4;
  cursor: pointer;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.pfi-text.expanded { display: block; white-space: pre-wrap; overflow: visible; }
.pfi-item-actions { display: flex; flex-direction: column; align-items: flex-end; gap: 0.4rem; flex-shrink: 0; }
.pfi-chevron {
  display: flex;
  padding: 2px;
  border: none;
  background: transparent;
  color: rgba(255, 255, 255, 0.5);
  cursor: pointer;
}
.pfi-chevron:hover { color: #fff; }
.pfi-chevron svg { transition: transform 0.15s; }

.pfi-spin {
  width: 1rem;
  height: 1rem;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: #fff;
  border-radius: 50%;
  animation: pfi-spin 0.7s linear infinite;
}
.pfi-spin.dark { border-color: rgba(255, 255, 255, 0.15); border-top-color: rgba(255, 255, 255, 0.7); width: 1.4rem; height: 1.4rem; }
@keyframes pfi-spin { to { transform: rotate(360deg); } }
@keyframes pfi-pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.5; } }
</style>
